import { useOutletContext, useParams } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { PanelsTopLeft } from 'lucide-react';

import { EmptyState } from '../components/states/index.js';
import { TAB_KINDS } from '../constants/profileTabs.js';
import IntelligenceTabPage from './IntelligenceTabPage.jsx';
import KnowledgeTabPage from './KnowledgeTabPage.jsx';
import HistoryTabPage from './HistoryTabPage.jsx';
import DashboardTabContent from './OverviewTabPage.jsx';
import ProfileDataListTab from './ProfileDataListTab.jsx';
import TasksTabPage from './TasksTabPage.jsx';

/**
 * Renders whichever tab the URL names, by dispatching on the tab's KIND.
 *
 * This is the seam that makes the tab set data-driven: the shell knows how to
 * render a handful of kinds, and the backend decides which of them appear, in
 * what order, under what labels, for which entity type.
 *
 * In practice there are three: Overview paints a dashboard of widgets and the
 * frontend owns it; Activity is the designed feed (CT-5527); every other
 * DYNAMIC tab is a LIST of data with filters and search, whatever the tenant
 * calls it.
 *
 * An unrecognised kind is expected, not exceptional — the backend will ship one
 * before the frontend learns it. It degrades to "nothing to show here" so a new
 * tab type never takes the profile down with it.
 */
const RENDERERS = {
  [TAB_KINDS.DASHBOARD]: DashboardTabContent,
  [TAB_KINDS.LIST]: ProfileDataListTab,
  // ACTIVITY renders History (CT-5724): Timeline (the CT-5527 feed) and From notes.
  [TAB_KINDS.ACTIVITY]: HistoryTabPage,
  [TAB_KINDS.INTELLIGENCE]: IntelligenceTabPage,
  [TAB_KINDS.TASKS]: TasksTabPage,
  [TAB_KINDS.KNOWLEDGE]: KnowledgeTabPage,
};

function ProfileTabPage() {
  const { t } = useTranslation('unified-profiles');
  const { tabId } = useParams();
  const context = useOutletContext();
  const tab = context.tabs.find((candidate) => candidate.id === tabId);

  const Renderer = tab ? RENDERERS[tab.kind] : undefined;

  if (!Renderer) {
    return (
      <div data-testid="unsupported-tab" className="flex min-h-0 flex-1 flex-col">
        <EmptyState
          icon={PanelsTopLeft}
          title={tab?.label ?? t('profile.unsupportedTab.title')}
          subtitle={t('profile.unsupportedTab.subtitle')}
        />
      </div>
    );
  }

  return (
    <div data-testid={`profile-tab-${tab.id}`} className="flex min-h-0 flex-1 flex-col">
      <Renderer tab={tab} />
    </div>
  );
}

export default ProfileTabPage;
